/**
 * The campaigns Morcast runs, as anyone sees them: the terms their manifests fix and where each
 * stands. Creators browse them to choose what to make.
 */

import type { Platform, YoutubeFormat } from "@morcast/campaigns";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client.js";
import type { Campaign, CampaignRequest } from "./requests.js";

/**
 * Where a campaign stands: before its window, in it, after it until it is settled, or settled or
 * refunded.
 */
export type CampaignPhase = "upcoming" | "running" | "ended" | "settled" | "refunded";

/** A campaign as `GET /api/campaigns` describes it. */
export interface PublicCampaign {
  chainId: number;
  escrow: string;
  /** The escrow's ID of the campaign. IDs restart in every deployment. */
  id: string;
  status: Campaign["status"];
  phase: CampaignPhase;
  brand: { name: string; wallet: string };
  platform: Platform;
  format: YoutubeFormat | null;
  metric: { unit: string; description: string };
  token: { address: string; symbol: string | null; decimals: number | null };
  /** Base units, as a decimal string. */
  budget: string;
  /** Target `T` in the primary metric. */
  target: string;
  /** Creator threshold `M`: below it a creator scores 0. */
  threshold: string;
  startAt: string;
  endAt: string;
  /** The most submissions each of a creator's accounts may make. */
  submissionLimit: number;
  /** The most accounts a creator may take part with. */
  accountLimit: number;
  brief: CampaignRequest["brief"];
  manifestHash: string;
  /** The manifest's permanent copy, outside Morcast's API. Null until it is written. */
  manifestCopyUrl: string | null;
  /** The manifest on Arweave, which does not depend on Morcast at all. Null until it is written. */
  manifestArchiveUrl: string | null;
  createdTx: string;
}

/** The address of a campaign's page. */
export function campaignPath(campaign: Pick<PublicCampaign, "escrow" | "id">): string {
  return `/campaigns/${campaign.escrow}/${campaign.id}`;
}

/** The campaigns Morcast runs, latest start first. */
export function useCampaigns() {
  return useQuery({
    queryKey: ["campaigns"],
    queryFn: async () =>
      (await api.get<{ campaigns: PublicCampaign[] }>("/api/campaigns")).campaigns,
  });
}

/** One campaign, by its escrow and ID. */
export function useCampaign(escrow: string, id: string) {
  return useQuery({
    queryKey: ["campaigns", escrow.toLowerCase(), id],
    queryFn: () => api.get<PublicCampaign>(`/api/campaigns/${escrow}/${id}`),
  });
}
